import { awaitMediaStreamReady, processWaveform } from '../analyser'
import { createWavArrayBuffer } from '../merge'
import { AzureSpeechRecognizer, PushStream } from './azureSTT'

const sampleRate = 16000

const mergeBuffers = (buffers: Float32Array[]) => {
  const totalLength = buffers.reduce((acc, buf) => acc + buf.length, 0)
  const totalBuffer = new Uint8Array(totalLength * 2)
  let offset = 0
  for (const buf of buffers) {
    totalBuffer.set(new Uint8Array(new Int16Array(buf.map((i) => i * 32767)).buffer), offset)
    offset += buf.length * 2
  }
  return totalBuffer
}

let stream
let ts = 0
async function AudioRecord(handler: (x: Float32Array) => any, setWaveFormData) {
  if (stream) throw new Error()
  setWaveFormData([])
  let lastPr: any
  const cts = ++ts
  if ((navigator as any).audioSession) (navigator as any).audioSession.type = 'play-and-record'
  stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      sampleSize: 16,
      channelCount: 1,
      echoCancellation: false,
      noiseSuppression: true,
    },
    video: false,
  })

  await new Promise<void>((res) => {
    const intv = setInterval(() => {
      if (stream.active && stream.getTracks().length && stream.getTracks()[0].readyState === 'live') {
        clearInterval(intv)
        res()
      }
    }, 10)
  })

  const audioCtx: AudioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate })
  const source = audioCtx.createMediaStreamSource(stream)

  const analyser = audioCtx.createAnalyser()
  source.connect(analyser)
  await awaitMediaStreamReady(analyser)
  setWaveFormData(processWaveform(analyser))

  await audioCtx.audioWorklet.addModule('/audio-processor-4.js')

  let handlerPr = Promise.resolve()
  const audioWorkletNode = new AudioWorkletNode(audioCtx, 'recorder-processor')
  audioWorkletNode.port.onmessage = async (event) => {
    if (event.data && event.data.type === 'data') {
      if (ts !== cts) {
        try {
          cleanup()
        } catch (e) {
          /* empty */
        }
        return
      }
      handlerPr = handlerPr.then(handler(event.data.buffer))
      if (lastPr) lastPr()
    }
  }
  source.connect(audioWorkletNode)

  analyser.fftSize = 64
  analyser.smoothingTimeConstant = 0.8
  const intv = setInterval(() => setWaveFormData(processWaveform(analyser)), 50)

  const cleanup = async () => {
    await handlerPr.catch(() => null)
    await new Promise((res) => setTimeout(res, (6400 / sampleRate) * 1000 + 200))
    await new Promise((res) => (lastPr = res))
    clearInterval(intv)
    stream?.getTracks().forEach((track) => track.stop())
    source.disconnect()
    audioWorkletNode.port.onmessage = null
    audioWorkletNode.disconnect()
    analyser.disconnect()
    try {
      audioCtx.close()
    } catch (e) {
      console.log(e)
    }
    if ((navigator as any).audioSession) (navigator as any).audioSession.type = 'playback'
    stream = null
  }
  return cleanup
}

let buffers: Float32Array[] = []
let stopAudioRecord
let stopSpeechRecognizer: (immetiate?: boolean) => Promise<string>
let stopPr: Promise<[string, ArrayBuffer | null]> = null
let running = false
let targetWordCount = 0
let rc = 0

export function startRecognition({
  setWaveFormData,
  setTranscription: transcriptionHandler,
  wordCount = 0,
  afterStart,
}: {
  setWaveFormData?: (wv: number[]) => void
  setTranscription?: (text: string) => void
  wordCount?: number
  afterStart?: () => void
} = {}) {
  let transcription = ''
  if (running) return Promise.resolve(false)
  running = true
  const cts = ++rc
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<Promise<boolean>>(async (res, rej) => {
    const timeout = setTimeout(() => {
      stopRecognition(false)
      rej()
    }, 10000)

    let idx = 0
    let pushStream: PushStream = null
    let pushStreamPr = Promise.resolve()

    buffers = []
    stopPr = null
    targetWordCount = wordCount

    let audioRec: Promise<() => Promise<void>>
    let off = 0

    const pushToStream = (buf: Float32Array) => {
      buffers.push(buf)
      off += buf.length
      if (!pushStream) return
      pushStreamPr = pushStreamPr.then(async () => {
        try {
          if (off > 1600) {
            await pushStream.writes(buffers.slice(idx)).then((r) => r)
            idx = buffers.length
            off = 0
          }
        } catch (e) {
          console.log(e)
          return
        }
      })
    }

    const setTranscription = (t: string) => transcriptionHandler?.((transcription = t))
    let resolveResult
    const pr = new Promise<boolean>((r) => {
      resolveResult = r
    })

    const azureSTTHandler = ([_stopAudioRecord, { pushStream: _pushStream, destroy: _stopSpeechRecognizer }]) => {
      if (_stopAudioRecord) {
        if (stopAudioRecord) stopAudioRecord(false)
        stopAudioRecord = (uploadAudio) => {
          running = false
          resolveResult(uploadAudio)
          return _stopAudioRecord()
        }
      }
      pushStream = _pushStream
      if (stopSpeechRecognizer) stopSpeechRecognizer()
      stopSpeechRecognizer = _stopSpeechRecognizer
      afterStart?.()
      res(pr)
      clearTimeout(timeout)
      if (cts !== rc) {
        stopRecognition(false)
        stopAudioRecord(false)
      }
    }

    const reject = () => {
      rej()
      audioRec.then((_stopAudioRecord) => _stopAudioRecord()).catch(() => null)
    }

    Promise.all([
      audioRec || (audioRec = AudioRecord(pushToStream, setWaveFormData)),
      AzureSpeechRecognizer({ setTranscription }),
    ])
      .then(azureSTTHandler)
      .catch(reject)
  })
}

export function stopRecognition(complete: boolean, afterStop?: () => void) {
  ++rc
  if (!stopAudioRecord || !stopSpeechRecognizer) {
    if (stopAudioRecord) {
      stopAudioRecord(false)
      stopAudioRecord = null
    }
    if (stopSpeechRecognizer) {
      stopSpeechRecognizer()
      stopSpeechRecognizer = null
    }
    running = false
    return stopPr || new Promise((r) => setTimeout(() => r(['', null]), 300))
  }
  const [_stopAudioRecord, _stopSpeechRecognizer] = [stopAudioRecord, stopSpeechRecognizer]
  stopAudioRecord = null
  stopSpeechRecognizer = null
  return (stopPr = new Promise<[string, ArrayBuffer | null]>((res, rej) => {
    _stopAudioRecord(complete)
      .then(() => _stopSpeechRecognizer())
      .then(async (text) => {
        afterStop?.()
        if (complete && buffers?.length) {
          const audio = createWavArrayBuffer(mergeBuffers(buffers), sampleRate)
          res([text, audio])
        } else {
          res([text, null])
        }
        buffers = []
      })
      .catch((e) => {
        rej(e)
        localStorage.azureToken = ''
      })
  }))
}
